import { ReactElement, ReactNode, useState } from 'react';

import styled from 'styled-components';

import { Collapse, CollapseProps } from '@components/animators/collapse';
import { Rotate } from '@components/animators/Rotate';
import { Badge } from '@components/badge';
import { IconProps } from '@components/icons';
import { IconDropRight } from '@components/icons/dropright';
import { IconErrorSmall } from '@components/icons/error-small';
import { InfoTooltip } from '@components/info-tooltip';
import { Text } from '@components/text';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import { Stack } from '../stack';

export const HIDE_CONTAINER_HEADER_CLASSNAME = 'hide-container-header';

export interface HideContainerProps {
  title: string;
  children: ReactNode;
  icon?: ReactElement<IconProps>;
  info?: string;
  defaultClosed?: boolean;
  isOpen?: boolean;
  error?: boolean;
  counter?: string | number;
  caps?: boolean;
  overflowType?: CollapseProps['overflowType'];
  onClick?: () => void;
}

export function HideContainer(props: HideContainerProps) {
  const {
    title,
    children,
    icon,
    info,
    defaultClosed = false,
    isOpen: controlledIsOpen,
    error = false,
    counter,
    caps = false,
    overflowType = 'auto',
    onClick,
  } = props;
  const isControlled = controlledIsOpen !== undefined;
  const [internalIsOpen, setInternalIsOpen] = useState(!defaultClosed);
  const isOpen = isControlled ? controlledIsOpen : internalIsOpen;

  return (
    <div>
      <div
        onClick={() => {
          if (!isControlled) {
            setInternalIsOpen(!isOpen);
          }
          onClick?.();
        }}
      >
        <StyledHeaderContainer>
          <Stack space={2} orientation="horizontal" align="center">
            {icon && <icon.type color="elementPurple50" {...icon.props} data-hover />}
            <Text font="semibold14" text={title} caps={caps} data-hover-text />
            {counter !== undefined && (
              <Badge size="md" style={{ padding: `${pxToRem(4)} ${pxToRem(8)}` }} text={counter} />
            )}
            {info && <InfoTooltip tooltip={info} />}
          </Stack>
          <Stack orientation="horizontal" space={2} align="center">
            {error && <IconErrorSmall color="error" />}
            <Rotate isRotated={isOpen}>
              <IconDropRight color="buttonIconPositive" data-hover-icon />
            </Rotate>
          </Stack>
        </StyledHeaderContainer>
      </div>
      <Collapse isCollapsed={!isOpen} keepCollapsedInDom overflowType={overflowType}>
        <ContentContainer>{children}</ContentContainer>
      </Collapse>
    </div>
  );
}

const StyledHeaderContainer = styled(Stack).attrs({
  orientation: 'horizontal',
  space: 1,
  className: HIDE_CONTAINER_HEADER_CLASSNAME,
})({
  position: 'relative',
  overflow: 'hidden',
  borderRadius: pxToRem(6),
  cursor: 'pointer',
  padding: `${pxToRem(4)} ${pxToRem(4)} ${pxToRem(4)} 0`,

  '&::before': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: `
        linear-gradient(
          90deg,
          transparent 0%,
          ${theme.color.uiTrans10} 100%
        )
      `,
    opacity: 0,
    transition: 'opacity 300ms ease-in-out',
  },

  '&:hover::before': {
    color: theme.color.textSecondary,
    opacity: 1,
  },

  '&:hover [data-hover-text]': {
    color: theme.color.textSecondary,
  },

  '&:hover [data-hover-icon]': {
    color: theme.color.buttonIconPositiveHover,
  },
});

const ContentContainer = styled.div({
  marginTop: pxToRem(16),
});
